/**
 * KAN-883: таблица "Лиды по дням": день, цифры показателей, доля лидов от
 * отправленных, строка "Итого". Каждая цифра - кнопка: открывает таблицу CRM
 * с этими лидами (onDrill; day null - весь период). Свежие дни сверху.
 */
import { formatCrmDayTitle } from '@/lib/crmBoard';
import { CRM_STATS_COLUMNS, crmLeadShare } from '@/lib/crmStats';
import { cn } from '@/lib/utils';
import type { CrmDailyMetric, CrmDailyMetrics, CrmDailyStats } from '@/types/crm';

interface CrmStatsTableProps {
  stats: CrmDailyStats;
  onDrill: (metric: CrmDailyMetric, day: string | null) => void;
}

const CELL = 'px-3 py-2 text-right tabular-nums';

function MetricCells({
  values,
  suffix,
  day,
  onDrill,
}: {
  values: CrmDailyMetrics;
  suffix: string;
  day: string | null;
  onDrill: CrmStatsTableProps['onDrill'];
}) {
  return (
    <>
      {CRM_STATS_COLUMNS.map(({ metric, label }) => (
        <MetricCell key={metric} metric={metric} label={label} values={values} suffix={suffix} day={day} onDrill={onDrill} />
      ))}
    </>
  );
}

function MetricCell({
  metric,
  label,
  values,
  suffix,
  day,
  onDrill,
}: {
  metric: CrmDailyMetric;
  label: string;
  values: CrmDailyMetrics;
  suffix: string;
  day: string | null;
  onDrill: CrmStatsTableProps['onDrill'];
}) {
  const value = values[metric];
  return (
    <>
      <td className={CELL}>
        <button
          type="button"
          data-testid={`crm-stats-cell-${metric}-${suffix}`}
          aria-label={`${label}: ${value}`}
          onClick={() => onDrill(metric, day)}
          className={cn('rounded px-1 hover:bg-blue-50 hover:text-blue-700', value === 0 && 'text-gray-400')}
        >
          {value}
        </button>
      </td>
      {metric === 'leads' && <td className={cn(CELL, 'text-gray-600')}>{crmLeadShare(values)}</td>}
    </>
  );
}

export function CrmStatsTable({ stats, onDrill }: CrmStatsTableProps): JSX.Element {
  const days = [...stats.days].reverse();
  return (
    <div className="overflow-x-auto rounded-lg border bg-white">
      <table data-testid="crm-stats-table" className="w-full min-w-[720px] text-sm">
        <thead className="border-b bg-gray-50 text-xs font-medium text-gray-500">
          <tr>
            <th scope="col" className="px-3 py-2 text-left">
              День
            </th>
            {CRM_STATS_COLUMNS.map(({ metric, label }) => (
              <HeaderCells key={metric} metric={metric} label={label} />
            ))}
          </tr>
        </thead>
        <tbody className="divide-y">
          {days.map((day) => (
            <tr key={day.date} data-testid={`crm-stats-row-${day.date}`} className="hover:bg-gray-50">
              <td className="whitespace-nowrap px-3 py-2 text-gray-900">{formatCrmDayTitle(day.date)}</td>
              <MetricCells values={day} suffix={day.date} day={day.date} onDrill={onDrill} />
            </tr>
          ))}
        </tbody>
        <tfoot className="border-t bg-gray-50 font-semibold">
          <tr data-testid="crm-stats-total">
            <td className="px-3 py-2">Итого</td>
            <MetricCells values={stats.totals} suffix="total" day={null} onDrill={onDrill} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function HeaderCells({ metric, label }: { metric: CrmDailyMetric; label: string }) {
  return (
    <>
      <th scope="col" className="whitespace-nowrap px-3 py-2 text-right">
        {label}
      </th>
      {metric === 'leads' && (
        <th scope="col" className="px-3 py-2 text-right" title="Лиды от отправленных">
          %
        </th>
      )}
    </>
  );
}
